// src/pages/landing-builder/StandardPageBuilder.jsx
import { useState, useEffect, useRef, useCallback } from 'react';
import { Button, Input } from '../../components/ui';
import {
  Layout, Type, Image, MousePointerClick, Clock, Code, Video, Zap, Quote,
  CreditCard, HelpCircle, Layers, Move, Plus, Trash2, Settings, ChevronUp,
  ChevronDown, GripVertical, X, Check, Play, Users, Bold, Italic, Underline,
  AlignLeft, AlignCenter, AlignRight, AlignJustify, List, ListOrdered, Link,
  Save, Strikethrough, Highlighter, Undo2, Redo2, Minus,
  RemoveFormatting, Columns,
} from 'lucide-react';
import {
  MAX_SECTIONS, TEXT_SIZES, ICON_MAP, getTextStyle,
  ColorPicker, TextSizeEditor,
} from './shared';

// ==================== SECTION TYPES ====================
export const SECTION_TYPES = {
  hero: {
    name: 'Hero', icon: Layout, description: 'Main headline section',
    defaultConfig: {
      logoUrl: '', logoWidth: 160,
      headline: 'Your Compelling Headline', headlineSize: 'h1', headlineColor: '',
      subheadline: 'A brief description that explains your offer', subheadlineSize: 'p', subheadlineColor: '',
      alignment: 'center', padding: 'large',
      backgroundType: 'color', backgroundColor: '#ffffff', backgroundImage: '',
      backgroundPosition: 'cover', backgroundOverlay: '', backgroundOverlayOpacity: 50,
    },
  },
  text: {
    name: 'Text Block', icon: Type, description: 'Rich text content',
    defaultConfig: {
      content: '<p>Add your content here.</p>', useRichText: true,
      textSize: 'p', textSizeCustom: '16', textColor: '', alignment: 'left', padding: 'medium',
      backgroundType: 'color', backgroundColor: '',
    },
  },
  image: {
    name: 'Image / Logo', icon: Image, description: 'Single image, banner, or logo',
    defaultConfig: {
      imageUrl: 'https://placehold.co/800x400/e2e8f0/64748b?text=Your+Image',
      alt: '', caption: '', fullWidth: true, rounded: false, width: 100, alignment: 'center',
      backgroundType: 'color', backgroundColor: '',
    },
  },
  cta: {
    name: 'Call to Action', icon: MousePointerClick, description: 'Button with optional A/B testing',
    defaultConfig: {
      text: 'Get Started Now', destinationUrl: '', isTestCta: false,
      style: 'solid', size: 'large', alignment: 'center', buttonColor: '', textColor: '',
      secondaryEnabled: false, secondaryText: 'Learn More', secondaryUrl: '', secondaryIsTestCta: false,
      backgroundType: 'color', backgroundColor: '',
    },
  },
  features: {
    name: 'Features', icon: Zap, description: 'Feature grid with icons',
    defaultConfig: {
      headline: 'Why Choose Us', headlineSize: 'h2', columns: 3, itemAlignment: 'center',
      items: [
        { icon: 'zap', title: 'Lightning Fast', description: 'Built for speed' },
        { icon: 'shield', title: 'Secure', description: 'Enterprise-grade security' },
        { icon: 'heart', title: 'Easy to Use', description: 'No coding required' },
      ],
      backgroundType: 'color', backgroundColor: '',
    },
  },
  testimonials: {
    name: 'Testimonials', icon: Quote, description: 'Customer reviews',
    defaultConfig: {
      headline: 'What Our Customers Say', headlineSize: 'h2',
      items: [
        { quote: 'This product changed my life!', author: 'John Doe', role: 'CEO', avatar: '' },
        { quote: 'Absolutely amazing experience.', author: 'Jane Smith', role: 'Director', avatar: '' },
      ],
      backgroundType: 'color', backgroundColor: '#f9fafb', cardBackgroundColor: '#ffffff',
    },
  },
  pricing: {
    name: 'Pricing', icon: CreditCard, description: 'Pricing table',
    defaultConfig: {
      headline: 'Simple, Transparent Pricing', headlineSize: 'h2',
      plans: [
        { name: 'Starter', price: '9', period: 'month', features: ['Feature 1', 'Feature 2'], cta: 'Get Started', ctaUrl: '', highlighted: false },
        { name: 'Pro', price: '29', period: 'month', features: ['All Starter features', 'Feature 3'], cta: 'Get Started', ctaUrl: '', highlighted: true },
      ],
      backgroundType: 'color', backgroundColor: '',
    },
  },
  faq: {
    name: 'FAQ', icon: HelpCircle, description: 'Frequently asked questions',
    defaultConfig: {
      headline: 'Frequently Asked Questions', headlineSize: 'h2',
      items: [
        { question: 'How does it work?', answer: 'Lorem ipsum dolor sit amet.' },
        { question: 'What is your refund policy?', answer: 'We offer a 30-day money-back guarantee.' },
      ],
      backgroundType: 'color', backgroundColor: '',
    },
  },
  video: {
    name: 'Video', icon: Video, description: 'Embedded video',
    defaultConfig: { videoUrl: '', headline: '', backgroundType: 'color', backgroundColor: '' },
  },
  countdown: {
    name: 'Countdown', icon: Clock, description: 'Urgency timer',
    defaultConfig: { headline: 'Offer Ends In', endDate: '', expiredText: 'Offer Expired', backgroundType: 'color', backgroundColor: '' },
  },
  divider: {
    name: 'Divider', icon: Layers, description: 'Visual separator',
    defaultConfig: { style: 'line', spacing: 'medium', color: '', backgroundType: 'color', backgroundColor: '' },
  },
  spacer: {
    name: 'Spacer', icon: Move, description: 'Empty space',
    defaultConfig: { height: 'medium', backgroundType: 'color', backgroundColor: '' },
  },
  container: {
    name: 'Container', icon: Columns, description: 'Multi-column layout block',
    defaultConfig: {
      columns: 2,
      gap: 'medium',
      alignment: 'top',
      padding: 'medium',
      backgroundType: 'color',
      backgroundColor: '',
      backgroundImage: '',
      link: '',
      // Each column holds an array of child section objects
      columnContent: [[], []],
    },
  },
  html: {
    name: 'Custom HTML', icon: Code, description: 'Embed custom HTML code',
    defaultConfig: { code: '<!-- Add your HTML -->\n<div style="padding:20px;text-align:center;">\n  <p>Custom HTML content</p>\n</div>', backgroundType: 'color', backgroundColor: '' },
  },
};

// ==================== LIVE COUNTDOWN ====================
const LiveCountdown = ({ endDate, expiredText, primaryColor }) => {
  const [timeLeft, setTimeLeft] = useState({ days: 0, hours: 0, mins: 0, secs: 0 });
  const [expired, setExpired] = useState(false);

  useEffect(() => {
    if (!endDate) return;
    const calc = () => {
      const diff = new Date(endDate).getTime() - Date.now();
      if (diff <= 0) { setExpired(true); return; }
      setTimeLeft({
        days:  Math.floor(diff / 86400000),
        hours: Math.floor((diff % 86400000) / 3600000),
        mins:  Math.floor((diff % 3600000) / 60000),
        secs:  Math.floor((diff % 60000) / 1000),
      });
    };
    calc();
    const iv = setInterval(calc, 1000);
    return () => clearInterval(iv);
  }, [endDate]);

  if (expired) return <p className="text-lg font-semibold text-center py-4">{expiredText || 'Offer Expired'}</p>;
  return (
    <div className="flex justify-center gap-2 sm:gap-4 flex-wrap">
      {[{ v: timeLeft.days, l: 'Days' }, { v: timeLeft.hours, l: 'Hours' }, { v: timeLeft.mins, l: 'Mins' }, { v: timeLeft.secs, l: 'Secs' }].map(({ v, l }) => (
        <div key={l} className="text-center min-w-[60px]">
          <div className="text-2xl sm:text-3xl font-bold rounded-lg px-3 py-2" style={{ backgroundColor: `${primaryColor}15`, color: primaryColor }}>
            {String(v).padStart(2, '0')}
          </div>
          <p className="text-xs mt-1 opacity-60">{l}</p>
        </div>
      ))}
    </div>
  );
};

// ==================== SECTION BACKGROUND WRAPPER ====================
const SectionBackground = ({ config, children }) => {
  const style = {};
  if (config.backgroundType === 'image' && config.backgroundImage) {
    style.backgroundImage = `url(${config.backgroundImage})`;
    style.backgroundSize = config.backgroundPosition || 'cover';
    style.backgroundPosition = 'center';
    style.backgroundRepeat = 'no-repeat';
  } else if (config.backgroundColor) {
    style.backgroundColor = config.backgroundColor;
  }
  return (
    <div style={style} className="relative">
      {config.backgroundType === 'image' && config.backgroundOverlay && (
        <div className="absolute inset-0" style={{ backgroundColor: config.backgroundOverlay, opacity: (config.backgroundOverlayOpacity || 50) / 100 }} />
      )}
      <div className="relative">{children}</div>
    </div>
  );
};

// ==================== BACKGROUND SETTINGS (local) ====================
const BackgroundSettings = ({ config, updateField }) => (
  <div className="space-y-3 pt-2 border-t">
    <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Section Background</p>
    <div className="flex gap-2">
      {['none', 'color', 'image'].map(t => (
        <button key={t} onClick={() => updateField('backgroundType', t)}
          className={`flex-1 py-1.5 border rounded text-xs capitalize ${config.backgroundType === t ? 'bg-primary text-white border-primary' : ''}`}>
          {t}
        </button>
      ))}
    </div>
    {config.backgroundType === 'color' && (
      <ColorPicker label="" value={config.backgroundColor} onChange={v => updateField('backgroundColor', v)} placeholder="#ffffff" />
    )}
    {config.backgroundType === 'image' && (
      <>
        <Input label="Image URL" value={config.backgroundImage || ''} onChange={e => updateField('backgroundImage', e.target.value)} placeholder="https://..." />
        <div>
          <label className="block text-xs font-medium mb-1">Image Size</label>
          <div className="flex gap-2">
            {['cover', 'contain', 'auto'].map(p => (
              <button key={p} onClick={() => updateField('backgroundPosition', p)}
                className={`flex-1 py-1.5 border rounded text-xs capitalize ${config.backgroundPosition === p ? 'bg-primary text-white border-primary' : ''}`}>
                {p}
              </button>
            ))}
          </div>
        </div>
        <ColorPicker label="Overlay Color" value={config.backgroundOverlay} onChange={v => updateField('backgroundOverlay', v)} />
        {config.backgroundOverlay && (
          <div>
            <label className="block text-xs font-medium mb-1">Overlay Opacity: {config.backgroundOverlayOpacity || 50}%</label>
            <input type="range" min="0" max="100" value={config.backgroundOverlayOpacity || 50}
              onChange={e => updateField('backgroundOverlayOpacity', parseInt(e.target.value))} className="w-full" />
          </div>
        )}
      </>
    )}
  </div>
);

// ==================== ADVANCED RICH TEXT EDITOR ====================
const SimpleRichTextEditor = ({ value, onChange }) => {
  const editorRef  = useRef(null);
  const savedRange = useRef(null);
  const linkInputRef = useRef(null);
  const [activeFormats, setActiveFormats] = useState({});
  const [blockFormat, setBlockFormat] = useState('p');
  const [showLinkBox, setShowLinkBox] = useState(false);
  const [linkUrl, setLinkUrl] = useState('https://');

  // ── Save the current selection range ────────────────────────────────────
  // Always saves even if empty (collapsed cursor) so lists work on the line
  const saveSelection = () => {
    const sel = window.getSelection();
    if (sel && sel.rangeCount > 0) {
      const range = sel.getRangeAt(0);
      // Only save if the anchor is inside the editor or there is no range yet
      if (editorRef.current?.contains(range.commonAncestorContainer)) {
        savedRange.current = range.cloneRange();
      }
    }
  };

  // ── Restore and re-apply the saved range ────────────────────────────────
  const restoreSelection = () => {
    if (!editorRef.current) return;
    editorRef.current.focus();
    if (!savedRange.current) return;
    const sel = window.getSelection();
    if (sel) {
      sel.removeAllRanges();
      try { sel.addRange(savedRange.current); } catch (_) {}
    }
  };

  // ── Run execCommand after restoring selection ────────────────────────────
  const exec = (cmd, val = null) => {
    restoreSelection();
    try { document.execCommand(cmd, false, val); } catch (_) {}
    if (editorRef.current) onChange(editorRef.current.innerHTML);
    updateActiveFormats();
  };

  // ── Format block (h1, h2, p, blockquote, pre) ───────────────────────────
  const execFormat = (tag) => {
    if (!tag) return;
    restoreSelection();
    // Wrap in angle brackets — formatBlock requires them in some browsers
    try { document.execCommand('formatBlock', false, tag); } catch (_) {}
    if (editorRef.current) onChange(editorRef.current.innerHTML);
    updateActiveFormats();
  };

  // ── Colour commands — must restore selection before firing ───────────────
  const applyColor = (cmd, color) => {
    restoreSelection();
    try { document.execCommand(cmd, false, color); } catch (_) {}
    if (editorRef.current) onChange(editorRef.current.innerHTML);
    updateActiveFormats();
  };

  // ── Link: use inline input instead of window.prompt ─────────────────────
  // window.prompt destroys the selection on many browsers. We show our own
  // small input box instead, which does not affect the document selection.
  const openLinkBox = () => {
    saveSelection();
    setLinkUrl('https://');
    setShowLinkBox(true);
    setTimeout(() => linkInputRef.current?.focus(), 50);
  };

  const confirmLink = () => {
    const url = linkUrl.trim();
    setShowLinkBox(false);
    if (!url || url === 'https://') return;
    restoreSelection();
    try { document.execCommand('createLink', false, url); } catch (_) {}
    // Make all links open in new tab
    editorRef.current?.querySelectorAll('a').forEach(a => {
      if (!a.target) { a.target = '_blank'; a.rel = 'noopener noreferrer'; }
    });
    if (editorRef.current) onChange(editorRef.current.innerHTML);
    updateActiveFormats();
  };

  const insertHR = () => {
    restoreSelection();
    try {
      document.execCommand('insertHTML', false,
        '<hr style="margin:1em 0;border:none;border-top:2px solid #e5e7eb;"/><p><br/></p>');
    } catch (_) {}
    if (editorRef.current) onChange(editorRef.current.innerHTML);
  };

  // ── Read active formats from the live selection ──────────────────────────
  const updateActiveFormats = () => {
    if (!editorRef.current) return;
    try {
      // Block format: walk up from selection to find the block tag
      let blockTag = 'p';
      const sel = window.getSelection();
      if (sel && sel.rangeCount > 0) {
        let node = sel.getRangeAt(0).startContainer;
        while (node && node !== editorRef.current) {
          const tag = node.nodeName?.toLowerCase();
          if (['h1','h2','h3','h4','blockquote','pre'].includes(tag)) { blockTag = tag; break; }
          node = node.parentNode;
        }
      }
      setBlockFormat(blockTag);
      setActiveFormats({
        bold:               document.queryCommandState('bold'),
        italic:             document.queryCommandState('italic'),
        underline:          document.queryCommandState('underline'),
        strikeThrough:      document.queryCommandState('strikeThrough'),
        justifyLeft:        document.queryCommandState('justifyLeft'),
        justifyCenter:      document.queryCommandState('justifyCenter'),
        justifyRight:       document.queryCommandState('justifyRight'),
        justifyFull:        document.queryCommandState('justifyFull'),
        insertUnorderedList: document.queryCommandState('insertUnorderedList'),
        insertOrderedList:   document.queryCommandState('insertOrderedList'),
      });
    } catch (_) {}
  };

  const handleInput   = () => { if (editorRef.current) onChange(editorRef.current.innerHTML); updateActiveFormats(); };
  const handleKeyUp   = () => { saveSelection(); updateActiveFormats(); };
  const handleMouseUp = () => { saveSelection(); updateActiveFormats(); };

  // Sync content from outside only when editor is not focused
  useEffect(() => {
    if (
      editorRef.current &&
      document.activeElement !== editorRef.current &&
      editorRef.current.innerHTML !== (value || '')
    ) {
      editorRef.current.innerHTML = value || '';
    }
  }, [value]);

  // ── ToolBtn: e.preventDefault() keeps focus in editor ───────────────────
  const ToolBtn = ({ cmd, title, children }) => (
    <button
      onMouseDown={e => { e.preventDefault(); saveSelection(); exec(cmd); }}
      title={title}
      className={`p-1.5 rounded transition-colors ${
        activeFormats[cmd] ? 'bg-primary text-white' : 'hover:bg-muted text-foreground'
      }`}
    >
      {children}
    </button>
  );

  const Sep = () => <div className="w-px bg-border mx-0.5 self-stretch my-1" />;

  return (
    <div className="border border-border rounded-lg overflow-hidden bg-card" dir="ltr">

      {/* ── Row 1: Block style · inline formatting · size · colour ── */}
      <div className="flex flex-wrap items-center gap-0.5 px-1.5 pt-1.5 pb-0.5 border-b border-border bg-card">

        {/* Block format — reflects current cursor position */}
        <select
          value={blockFormat}
          onMouseDown={saveSelection}
          onChange={e => { const v = e.target.value; if (v) execFormat(v); }}
          className="text-xs border border-border rounded px-1 py-1 bg-card text-foreground h-7 mr-0.5"
        >
          <option value="p">Paragraph</option>
          <option value="h1">Heading 1</option>
          <option value="h2">Heading 2</option>
          <option value="h3">Heading 3</option>
          <option value="h4">Heading 4</option>
          <option value="blockquote">Blockquote</option>
          <option value="pre">Code Block</option>
        </select>

        <Sep />
        <ToolBtn cmd="bold"          title="Bold (Ctrl+B)"><Bold          className="h-3.5 w-3.5" /></ToolBtn>
        <ToolBtn cmd="italic"        title="Italic (Ctrl+I)"><Italic       className="h-3.5 w-3.5" /></ToolBtn>
        <ToolBtn cmd="underline"     title="Underline (Ctrl+U)"><Underline className="h-3.5 w-3.5" /></ToolBtn>
        <ToolBtn cmd="strikeThrough" title="Strikethrough"><Strikethrough  className="h-3.5 w-3.5" /></ToolBtn>
        <Sep />

        {/* Font size — free text input, any value e.g. 14px, 1.2em, 2rem */}
        <input
          type="text"
          placeholder="Size"
          title="Font size (e.g. 16px, 1.2em, 2rem)"
          onMouseDown={saveSelection}
          onKeyDown={e => {
            if (e.key === 'Enter') {
              e.preventDefault();
              const val = e.target.value.trim();
              if (val) {
                restoreSelection();
                // execCommand fontSize only accepts 1-7; use insertHTML with inline style instead
                document.execCommand('insertHTML', false,
                  `<span style="font-size:${/^\d+$/.test(val) ? val + 'px' : val}">${window.getSelection()?.toString() || ''}</span>`
                );
                // If nothing selected, wrap cursor with a styled span
                if (!window.getSelection()?.toString()) {
                  restoreSelection();
                  document.execCommand('fontSize', false, '3'); // placeholder
                  const spans = editorRef.current?.querySelectorAll('font[size="3"]');
                  spans?.forEach(s => {
                    s.removeAttribute('size');
                    s.style.fontSize = /^\d+$/.test(val) ? val + 'px' : val;
                  });
                }
                if (editorRef.current) onChange(editorRef.current.innerHTML);
                updateActiveFormats();
              }
            }
          }}
          onBlur={e => {
            const val = e.target.value.trim();
            if (!val) return;
            restoreSelection();
            document.execCommand('fontSize', false, '3');
            const spans = editorRef.current?.querySelectorAll('font[size="3"]');
            spans?.forEach(s => {
              s.removeAttribute('size');
              s.style.fontSize = /^\d+$/.test(val) ? val + 'px' : val;
            });
            if (editorRef.current) onChange(editorRef.current.innerHTML);
          }}
          className="text-xs border border-border rounded px-1.5 py-1 bg-card text-foreground h-7 w-16 focus:outline-none focus:ring-1 focus:ring-primary"
        />
        <Sep />

        {/* Text colour */}
        <label title="Text Color" onMouseDown={saveSelection}
          className="cursor-pointer flex items-center gap-0.5 p-1 hover:bg-muted rounded text-foreground text-xs">
          <span className="font-bold text-sm leading-none" style={{ fontFamily: 'serif' }}>A</span>
          <input type="color" defaultValue="#000000" className="w-0 h-0 opacity-0 absolute"
            onChange={e => applyColor('foreColor', e.target.value)} />
        </label>

        {/* Highlight colour */}
        <label title="Highlight Color" onMouseDown={saveSelection}
          className="cursor-pointer flex items-center p-1 hover:bg-muted rounded text-xs">
          <Highlighter className="h-3.5 w-3.5 text-foreground" />
          <input type="color" defaultValue="#ffff00" className="w-0 h-0 opacity-0 absolute"
            onChange={e => applyColor('hiliteColor', e.target.value)} />
        </label>

        <Sep />
        <button onMouseDown={e => { e.preventDefault(); saveSelection(); exec('removeFormat'); }}
          title="Clear Formatting" className="p-1.5 rounded hover:bg-muted text-foreground">
          <RemoveFormatting className="h-3.5 w-3.5" />
        </button>
        <Sep />
        <button onMouseDown={e => { e.preventDefault(); exec('undo'); }}  title="Undo" className="p-1.5 rounded hover:bg-muted text-foreground"><Undo2 className="h-3.5 w-3.5" /></button>
        <button onMouseDown={e => { e.preventDefault(); exec('redo'); }}  title="Redo" className="p-1.5 rounded hover:bg-muted text-foreground"><Redo2 className="h-3.5 w-3.5" /></button>
      </div>

      {/* ── Row 2: Alignment · lists · indent · link · HR ── */}
      <div className="flex flex-wrap items-center gap-0.5 px-1.5 pb-1.5 pt-0.5 border-b border-border bg-card">
        <ToolBtn cmd="justifyLeft"         title="Align Left"><AlignLeft      className="h-3.5 w-3.5" /></ToolBtn>
        <ToolBtn cmd="justifyCenter"       title="Center"><AlignCenter        className="h-3.5 w-3.5" /></ToolBtn>
        <ToolBtn cmd="justifyRight"        title="Align Right"><AlignRight    className="h-3.5 w-3.5" /></ToolBtn>
        <ToolBtn cmd="justifyFull"         title="Justify"><AlignJustify      className="h-3.5 w-3.5" /></ToolBtn>
        <Sep />
        <ToolBtn cmd="insertUnorderedList" title="Bullet List"><List          className="h-3.5 w-3.5" /></ToolBtn>
        <ToolBtn cmd="insertOrderedList"   title="Numbered List"><ListOrdered className="h-3.5 w-3.5" /></ToolBtn>
        <Sep />
        <button onMouseDown={e => { e.preventDefault(); saveSelection(); exec('indent');  }} title="Indent"  className="p-1.5 rounded hover:bg-muted text-foreground text-sm">→</button>
        <button onMouseDown={e => { e.preventDefault(); saveSelection(); exec('outdent'); }} title="Outdent" className="p-1.5 rounded hover:bg-muted text-foreground text-sm">←</button>
        <Sep />
        {/* Link button — opens inline input (NOT window.prompt) */}
        <button onMouseDown={e => { e.preventDefault(); openLinkBox(); }}
          title="Insert Link" className="p-1.5 rounded hover:bg-muted text-foreground">
          <Link className="h-3.5 w-3.5" />
        </button>
        <button onMouseDown={e => { e.preventDefault(); saveSelection(); exec('unlink'); }}
          title="Remove Link" className="p-1.5 rounded hover:bg-muted text-foreground opacity-60">
          <Link className="h-3.5 w-3.5 opacity-50" />
        </button>
        <Sep />
        <button onMouseDown={e => { e.preventDefault(); insertHR(); }} title="Insert Divider" className="p-1.5 rounded hover:bg-muted text-foreground">
          <Minus className="h-3.5 w-3.5" />
        </button>
      </div>

      {/* ── Inline link input — shown instead of window.prompt ── */}
      {showLinkBox && (
        <div className="flex items-center gap-2 px-2 py-1.5 border-b border-border bg-muted/40">
          <Link className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" />
          <input
            ref={linkInputRef}
            type="url"
            value={linkUrl}
            onChange={e => setLinkUrl(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); confirmLink(); } if (e.key === 'Escape') setShowLinkBox(false); }}
            placeholder="https://example.com"
            className="flex-1 text-xs px-2 py-1 border border-border rounded bg-card text-foreground focus:outline-none focus:ring-1 focus:ring-primary"
          />
          <button onClick={confirmLink}
            className="px-3 py-1 text-xs bg-primary text-white rounded hover:bg-primary/90">
            Add
          </button>
          <button onClick={() => setShowLinkBox(false)}
            className="px-2 py-1 text-xs text-muted-foreground hover:bg-muted rounded">
            Cancel
          </button>
        </div>
      )}

      {/* ── Editable content area ── */}
      <div
        ref={editorRef}
        contentEditable
        suppressContentEditableWarning
        onInput={handleInput}
        onKeyUp={handleKeyUp}
        onMouseUp={handleMouseUp}
        onFocus={updateActiveFormats}
        className="min-h-[160px] max-h-[360px] overflow-y-auto p-3 focus:outline-none prose prose-sm max-w-none text-sm bg-card text-foreground"
        dir="ltr"
        style={{ direction: 'ltr' }}
      />
    </div>
  );
};

// ==================== CONTAINER SECTION ====================
// Renders 1-4 columns side by side (desktop) / stacked (mobile).
// Each column accepts dragged-in child sections.

const CONTAINER_GAP = { none: 'gap-0', small: 'gap-2', medium: 'gap-4', large: 'gap-8' };
const CONTAINER_PAD = { none: 'py-0 px-0', small: 'py-4 px-4', medium: 'py-8 px-6', large: 'py-16 px-8' };
const CONTAINER_ALIGN = { top: 'items-start', center: 'items-center', bottom: 'items-end', stretch: 'items-stretch' };
const GRID_COLS = { 1: 'grid-cols-1', 2: 'grid-cols-1 md:grid-cols-2', 3: 'grid-cols-1 md:grid-cols-3', 4: 'grid-cols-1 md:grid-cols-2 lg:grid-cols-4' };

// ── Column drop zone — accepts dragged sections ─────────────────────────────
const ColumnDropZone = ({ children, colIdx, onDrop, onDragOver, isDragOver }) => (
  <div
    className={`flex-1 min-h-[80px] rounded-lg border-2 border-dashed transition-colors ${
      isDragOver ? 'border-primary bg-primary/5' : 'border-border/40 bg-muted/10'
    }`}
    onDragOver={e => { e.preventDefault(); e.stopPropagation(); onDragOver(colIdx); }}
    onDrop={e => { e.preventDefault(); e.stopPropagation(); onDrop(colIdx, e); }}
  >
    {children}
    {!children?.length && (
      <div className="flex items-center justify-center h-16 text-xs text-muted-foreground/50">
        Drop element here
      </div>
    )}
  </div>
);

// ── Container renderer for the live preview ─────────────────────────────────
export const ContainerRenderer = ({ section, theme, onColumnDrop, onColumnDragOver, dragOverCol, editingContainerCol, setEditingContainerCol, onRemoveChild }) => {
  const config = section.config || {};
  const cols   = Math.min(Math.max(config.columns || 2, 1), 4);
  const colContent = config.columnContent || Array.from({ length: cols }, () => []);

  const bg = config.backgroundType === 'image' && config.backgroundImage
    ? { backgroundImage: `url(${config.backgroundImage})`, backgroundSize: 'cover', backgroundPosition: 'center' }
    : { backgroundColor: config.backgroundColor || 'transparent' };

  const inner = (
    <div
      className={`w-full max-w-[800px] mx-auto ${CONTAINER_PAD[config.padding] || CONTAINER_PAD.medium}`}
      style={bg}
    >
      <div className={`grid ${GRID_COLS[cols]} ${CONTAINER_GAP[config.gap] || CONTAINER_GAP.medium} ${CONTAINER_ALIGN[config.alignment] || CONTAINER_ALIGN.top}`}>
        {Array.from({ length: cols }).map((_, colIdx) => {
          const children = (colContent[colIdx] || []);
          return (
            <ColumnDropZone
              key={colIdx}
              colIdx={colIdx}
              onDrop={onColumnDrop}
              onDragOver={onColumnDragOver}
              isDragOver={dragOverCol === colIdx}
            >
              {children.map((child, childIdx) => (
                <div key={child.id || childIdx} className="relative group/child border border-transparent hover:border-primary/30 rounded">
                  <SectionRenderer section={child} config={child.config} theme={theme} />
                  {onRemoveChild && (
                    <button
                      onClick={() => onRemoveChild(colIdx, childIdx)}
                      className="absolute top-1 right-1 opacity-0 group-hover/child:opacity-100 p-0.5 bg-red-500 text-white rounded text-xs"
                      title="Remove"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  )}
                </div>
              ))}
            </ColumnDropZone>
          );
        })}
      </div>
    </div>
  );

  return config.link
    ? <a href={config.link} target="_blank" rel="noopener noreferrer" className="block">{inner}</a>
    : inner;
};

// ── Container settings editor ─────────────────────────────────────────────
export const ContainerEditor = ({ config, onUpdate }) => {
  const update = (k, v) => onUpdate({ ...config, [k]: v });

  const setColumns = (n) => {
    const cur = config.columnContent || [];
    const next = Array.from({ length: n }, (_, i) => cur[i] || []);
    onUpdate({ ...config, columns: n, columnContent: next });
  };

  return (
    <div className="space-y-4 p-4">
      <h4 className="font-semibold text-sm">Container Settings</h4>

      {/* Columns */}
      <div>
        <label className="block text-xs font-medium text-muted-foreground mb-1.5">Columns</label>
        <div className="flex gap-1.5">
          {[1, 2, 3, 4].map(n => (
            <button key={n} onClick={() => setColumns(n)}
              className={`flex-1 py-1.5 border rounded text-sm font-medium transition-colors ${config.columns === n ? 'bg-primary text-white border-primary' : 'hover:bg-muted'}`}>
              {n}
            </button>
          ))}
        </div>
      </div>

      {/* Gap */}
      <div>
        <label className="block text-xs font-medium text-muted-foreground mb-1.5">Column Gap</label>
        <div className="flex gap-1.5">
          {['none','small','medium','large'].map(g => (
            <button key={g} onClick={() => update('gap', g)}
              className={`flex-1 py-1.5 border rounded text-xs capitalize transition-colors ${config.gap === g ? 'bg-primary text-white border-primary' : 'hover:bg-muted'}`}>
              {g}
            </button>
          ))}
        </div>
      </div>

      {/* Vertical alignment */}
      <div>
        <label className="block text-xs font-medium text-muted-foreground mb-1.5">Vertical Alignment</label>
        <div className="flex gap-1.5">
          {['top','center','bottom','stretch'].map(a => (
            <button key={a} onClick={() => update('alignment', a)}
              className={`flex-1 py-1.5 border rounded text-xs capitalize transition-colors ${config.alignment === a ? 'bg-primary text-white border-primary' : 'hover:bg-muted'}`}>
              {a}
            </button>
          ))}
        </div>
      </div>

      {/* Padding */}
      <div>
        <label className="block text-xs font-medium text-muted-foreground mb-1.5">Padding</label>
        <div className="flex gap-1.5">
          {['none','small','medium','large'].map(p => (
            <button key={p} onClick={() => update('padding', p)}
              className={`flex-1 py-1.5 border rounded text-xs capitalize transition-colors ${config.padding === p ? 'bg-primary text-white border-primary' : 'hover:bg-muted'}`}>
              {p}
            </button>
          ))}
        </div>
      </div>

      {/* Background colour */}
      <div>
        <label className="block text-xs font-medium text-muted-foreground mb-1.5">Background Color</label>
        <div className="flex gap-2">
          <input type="color" value={config.backgroundColor || '#ffffff'}
            onChange={e => update('backgroundColor', e.target.value)}
            className="w-8 h-8 rounded border cursor-pointer flex-shrink-0" />
          <input type="text" value={config.backgroundColor || ''}
            onChange={e => update('backgroundColor', e.target.value)}
            placeholder="transparent"
            className="flex-1 px-2 py-1 border border-border rounded text-xs bg-card text-foreground" />
          {config.backgroundColor && (
            <button onClick={() => update('backgroundColor', '')}
              className="text-xs text-muted-foreground hover:text-foreground px-1">✕</button>
          )}
        </div>
      </div>

      {/* Background image */}
      <div>
        <label className="block text-xs font-medium text-muted-foreground mb-1.5">Background Image URL</label>
        <input type="url" value={config.backgroundImage || ''}
          onChange={e => update('backgroundImage', e.target.value)}
          placeholder="https://..."
          className="w-full px-2 py-1.5 border border-border rounded text-xs bg-card text-foreground" />
      </div>

      {/* Link */}
      <div>
        <label className="block text-xs font-medium text-muted-foreground mb-1.5">Container Link (optional)</label>
        <input type="url" value={config.link || ''}
          onChange={e => update('link', e.target.value)}
          placeholder="https://..."
          className="w-full px-2 py-1.5 border border-border rounded text-xs bg-card text-foreground" />
        <p className="text-[10px] text-muted-foreground mt-1">Wraps the whole container in a link.</p>
      </div>
    </div>
  );
};

// ==================== SECTION RENDERER ====================
export const SectionRenderer = ({ section, config: rawConfig, theme }) => {
  const primaryColor = theme?.primaryColor || '#10B981';
  const sectionType = SECTION_TYPES[section?.type];
  const config = { ...(sectionType?.defaultConfig || {}), ...(rawConfig || {}) };
  const getIcon = (name) => { const Icon = ICON_MAP[name] || Zap; return <Icon className="h-6 w-6" />; };
  if (!section?.type) return null;

  switch (section.type) {
    case 'hero':
      return (
        <SectionBackground config={config}>
          <div className={`${config.padding === 'large' ? 'py-20' : config.padding === 'small' ? 'py-8' : 'py-12'} px-6`}>
            <div className={`max-w-[800px] mx-auto ${config.alignment === 'center' ? 'text-center' : config.alignment === 'right' ? 'text-right' : 'text-left'}`}>
              {config.logoUrl && (
                <img src={config.logoUrl} alt="Logo"
                  style={{ width: config.logoWidth || 160, maxWidth: '100%' }}
                  className={`h-auto mb-6 ${config.alignment === 'center' ? 'mx-auto' : config.alignment === 'right' ? 'ml-auto' : ''}`}
                />
              )}
              <h1 style={getTextStyle(config.headlineSize, config.headlineSizeCustom, config.headlineColor)}>
                {config.headline || 'Headline'}
              </h1>
              {config.subheadline && (
                <p className="mt-4 opacity-80" style={getTextStyle(config.subheadlineSize, config.subheadlineSizeCustom, config.subheadlineColor)}>
                  {config.subheadline}
                </p>
              )}
            </div>
          </div>
        </SectionBackground>
      );

    case 'text':
      return (
        <SectionBackground config={config}>
          <div className={`${config.padding === 'large' ? 'py-16' : config.padding === 'small' ? 'py-6' : 'py-10'} px-6`}>
            <div className={`max-w-[800px] mx-auto ${config.alignment === 'center' ? 'text-center' : config.alignment === 'right' ? 'text-right' : 'text-left'}`}>
              {config.useRichText
                ? <div className="prose prose-sm max-w-none" style={{ color: config.textColor || undefined }} dangerouslySetInnerHTML={{ __html: config.content || '' }} />
                : <p style={getTextStyle(config.textSize, config.textSizeCustom, config.textColor)} className="leading-relaxed whitespace-pre-wrap">{config.content || ''}</p>
              }
            </div>
          </div>
        </SectionBackground>
      );

    case 'image':
      return (
        <SectionBackground config={config}>
          <div className="py-6 px-6">
            <div className="max-w-[800px] mx-auto">
              <div className={`${config.alignment === 'center' ? 'mx-auto' : config.alignment === 'right' ? 'ml-auto' : 'mr-auto'}`}
                style={{ maxWidth: config.fullWidth ? '100%' : `${config.width || 100}%`, width: `${config.width || 100}%` }}>
                <img src={config.imageUrl || 'https://placehold.co/800x400'} alt={config.alt || ''}
                  className={`w-full ${config.rounded ? 'rounded-lg' : ''}`} />
                {config.caption && <p className="text-center text-sm opacity-60 mt-2">{config.caption}</p>}
              </div>
            </div>
          </div>
        </SectionBackground>
      );

    case 'cta': {
      const btnColor = config.buttonColor || primaryColor;
      return (
        <SectionBackground config={config}>
          <div className={`py-8 ${config.alignment === 'center' ? 'text-center' : config.alignment === 'right' ? 'text-right' : 'text-left'}`}>
            <div className={`flex gap-4 flex-wrap px-6 max-w-[800px] mx-auto ${config.alignment === 'center' ? 'justify-center' : config.alignment === 'right' ? 'justify-end' : 'justify-start'}`}>
              <button
                className={`font-semibold rounded-lg transition-all relative ${config.size === 'small' ? 'px-4 py-2 text-sm' : config.size === 'large' ? 'px-8 py-4 text-lg' : 'px-6 py-3'}`}
                style={{ backgroundColor: config.style === 'solid' ? btnColor : 'transparent', color: config.style === 'solid' ? (config.textColor || '#fff') : btnColor, border: `2px solid ${btnColor}` }}
              >
                {config.text || 'Get Started'}
                {config.isTestCta && <span className="absolute -top-2 -right-2 bg-purple-500/100 text-white text-xs px-1.5 py-0.5 rounded-full">A/B</span>}
              </button>
              {config.secondaryEnabled && (
                <button className="px-6 py-3 font-semibold rounded-lg relative" style={{ backgroundColor: 'transparent', color: btnColor, border: `2px solid ${btnColor}` }}>
                  {config.secondaryText || 'Learn More'}
                  {config.secondaryIsTestCta && <span className="absolute -top-2 -right-2 bg-purple-500/100 text-white text-xs px-1.5 py-0.5 rounded-full">A/B</span>}
                </button>
              )}
            </div>
          </div>
        </SectionBackground>
      );
    }

    case 'features':
      return (
        <SectionBackground config={config}>
          <div className="py-16 px-6">
            <div className="max-w-[800px] mx-auto">
              {config.headline && <h2 className="font-bold text-center mb-12" style={getTextStyle(config.headlineSize)}>{config.headline}</h2>}
              <div className={`grid gap-8 ${config.columns === 2 ? 'md:grid-cols-2' : config.columns === 4 ? 'md:grid-cols-4' : 'md:grid-cols-3'}`}>
                {config.items?.map((item, i) => (
                  <div key={i} className={`p-6 ${config.itemAlignment === 'left' ? 'text-left' : 'text-center'}`}>
                    <div className={`inline-flex items-center justify-center w-12 h-12 rounded-full mb-4 ${config.itemAlignment === 'left' ? '' : ''}`}
                      style={{ backgroundColor: `${primaryColor}20`, color: primaryColor }}>
                      {getIcon(item.icon)}
                    </div>
                    <h3 className="text-lg font-semibold mb-2">{item.title}</h3>
                    <p className="opacity-70">{item.description}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </SectionBackground>
      );

    case 'testimonials':
      return (
        <SectionBackground config={config}>
          <div className="py-16 px-6">
            <div className="max-w-[800px] mx-auto">
              {config.headline && <h2 className="font-bold text-center mb-12" style={getTextStyle(config.headlineSize)}>{config.headline}</h2>}
              <div className="grid gap-8 md:grid-cols-2">
                {config.items?.map((item, i) => (
                  <div key={i} className="p-6 rounded-lg shadow-sm" style={{ backgroundColor: config.cardBackgroundColor || '#ffffff' }}>
                    <Quote className="h-8 w-8 mb-4 opacity-20" />
                    <p className="text-lg mb-4 italic">"{item.quote}"</p>
                    <div className="flex items-center gap-3">
                      {item.avatar ? <img src={item.avatar} alt={item.author} className="w-10 h-10 rounded-full object-cover" /> : <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center font-semibold text-foreground">{item.author?.[0]}</div>}
                      <div><p className="font-semibold">{item.author}</p><p className="text-sm opacity-60">{item.role}</p></div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </SectionBackground>
      );

    case 'pricing':
      return (
        <SectionBackground config={config}>
          <div className="py-16 px-6">
            <div className="max-w-[800px] mx-auto">
              {config.headline && <h2 className="font-bold text-center mb-12" style={getTextStyle(config.headlineSize)}>{config.headline}</h2>}
              <div className={`grid gap-8 ${config.plans?.length === 2 ? 'md:grid-cols-2' : 'md:grid-cols-3'}`}>
                {config.plans?.map((plan, i) => (
                  <div key={i} className={`p-6 rounded-xl border-2 ${plan.highlighted ? 'shadow-xl' : 'border-border'}`}
                    style={{ borderColor: plan.highlighted ? primaryColor : undefined, backgroundColor: plan.backgroundColor || '#ffffff' }}>
                    <h3 className="text-xl font-bold mb-2">{plan.name}</h3>
                    <div className="mb-4"><span className="text-4xl font-bold">${plan.price}</span><span className="opacity-60">/{plan.period}</span></div>
                    <ul className="space-y-2 mb-6">{plan.features?.map((f, j) => <li key={j} className="flex items-center gap-2"><Check className="h-4 w-4" style={{ color: primaryColor }} />{f}</li>)}</ul>
                    <button className="w-full py-2 rounded-lg font-semibold"
                      style={{ backgroundColor: plan.highlighted ? primaryColor : 'transparent', color: plan.highlighted ? '#fff' : primaryColor, border: `2px solid ${primaryColor}` }}>
                      {plan.cta}
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </SectionBackground>
      );

    case 'faq':
      return (
        <SectionBackground config={config}>
          <div className="py-16 px-6">
            <div className="max-w-[800px] mx-auto">
              {config.headline && <h2 className="font-bold text-center mb-12" style={getTextStyle(config.headlineSize)}>{config.headline}</h2>}
              <div className="space-y-4">
                {config.items?.map((item, i) => (
                  <div key={i} className="border rounded-lg p-4">
                    <h4 className="font-semibold">{item.question}</h4>
                    <p className="mt-2 opacity-70">{item.answer}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </SectionBackground>
      );

    case 'video':
      return (
        <SectionBackground config={config}>
          <div className="py-12 px-6">
            <div className="max-w-[800px] mx-auto">
              {config.headline && <h3 className="text-2xl font-bold text-center mb-6">{config.headline}</h3>}
              {config.videoUrl
                ? <div className="aspect-video"><iframe src={config.videoUrl} className="w-full h-full rounded-lg" allowFullScreen title="Video" /></div>
                : <div className="aspect-video bg-muted rounded-lg flex items-center justify-center"><Play className="h-16 w-16 opacity-30" /></div>
              }
            </div>
          </div>
        </SectionBackground>
      );

    case 'countdown':
      return (
        <SectionBackground config={config}>
          <div className="py-8 px-6 text-center">
            <div className="max-w-[800px] mx-auto">
              {config.headline && <p className="text-lg mb-4 font-medium">{config.headline}</p>}
              {config.endDate
                ? <LiveCountdown endDate={config.endDate} expiredText={config.expiredText} primaryColor={primaryColor} />
                : <div className="flex justify-center gap-4">{['Days', 'Hours', 'Mins', 'Secs'].map(u => <div key={u} className="text-center"><div className="text-3xl font-bold rounded-lg px-4 py-2" style={{ backgroundColor: `${primaryColor}15`, color: primaryColor }}>00</div><p className="text-xs mt-1 opacity-60">{u}</p></div>)}</div>
              }
            </div>
          </div>
        </SectionBackground>
      );

    case 'divider':
      return (
        <SectionBackground config={config}>
          <div className={`${config.spacing === 'large' ? 'py-8' : config.spacing === 'small' ? 'py-2' : 'py-4'}`}>
            {config.style === 'line' && <hr style={{ borderColor: config.color || '#e5e7eb' }} />}
            {config.style === 'dots' && <p className="text-center" style={{ color: config.color || '#d1d5db' }}>• • •</p>}
          </div>
        </SectionBackground>
      );

    case 'spacer':
      return <SectionBackground config={config}><div className={`${config.height === 'large' ? 'h-24' : config.height === 'small' ? 'h-8' : 'h-16'}`} /></SectionBackground>;

    case 'html':
      return (
        <SectionBackground config={config}>
          <div className="py-4 px-6"><div className="max-w-[800px] mx-auto"><div dangerouslySetInnerHTML={{ __html: config.code || '' }} /></div></div>
        </SectionBackground>
      );

    case 'container':
      // ContainerRenderer is wired up in StandardPagePreview with drop handlers
      // For SectionRenderer (used in nested/child contexts) render a static version
      return (
        <ContainerRenderer section={section} theme={theme} />
      );

    default:
      return <div className="p-8 text-center text-muted-foreground">Unknown section type: {section.type}</div>;
  }
};

// ==================== SECTION EDITOR ====================
export const SectionEditor = ({ section, onUpdate, onClose }) => {
  const config = section.config || {};
  const updateField = (k, v) => onUpdate({ ...section, config: { ...config, [k]: v } });
  const updateArrayItem = (arrayKey, i, field, v) => {
    const arr = [...(config[arrayKey] || [])]; arr[i] = { ...arr[i], [field]: v }; updateField(arrayKey, arr);
  };
  const addArrayItem = (arrayKey, item) => updateField(arrayKey, [...(config[arrayKey] || []), item]);
  const removeArrayItem = (arrayKey, i) => updateField(arrayKey, (config[arrayKey] || []).filter((_, j) => j !== i));

  // Countdown: local pending date state with Apply button
  const [pendingEndDate, setPendingEndDate] = useState(config.endDate || '');

  return (
    <div className="h-full flex flex-col">
      <div className="flex items-center justify-between p-4 border-b flex-shrink-0">
        <h3 className="font-semibold text-sm">{SECTION_TYPES[section.type]?.name || section.type}</h3>
        <button onClick={onClose} className="p-1 hover:bg-muted rounded"><X className="h-4 w-4" /></button>
      </div>

      <div className="flex-1 overflow-y-auto p-4 space-y-4">

        {/* ── HERO ── */}
        {section.type === 'hero' && (<>
          <div>
            <label className="block text-sm font-medium mb-1">Logo</label>
            <Input label="" value={config.logoUrl || ''} onChange={e => updateField('logoUrl', e.target.value)} placeholder="https://... (leave blank to hide)" />
            {config.logoUrl && (
              <div className="mt-2">
                <label className="block text-xs font-medium mb-1">Logo Width: {config.logoWidth || 160}px</label>
                <input type="range" min="40" max="400" value={config.logoWidth || 160}
                  onChange={e => updateField('logoWidth', parseInt(e.target.value))} className="w-full" />
              </div>
            )}
          </div>
          <Input label="Headline" value={config.headline || ''} onChange={e => updateField('headline', e.target.value)} />
          <TextSizeEditor label="Headline Size" value={config.headlineSize} customValue={config.headlineSizeCustom} onChange={v => updateField('headlineSize', v)} onCustomChange={v => updateField('headlineSizeCustom', v)} />
          <ColorPicker label="Headline Color" value={config.headlineColor} onChange={v => updateField('headlineColor', v)} />
          <div><label className="block text-sm font-medium mb-1">Subheadline</label><textarea value={config.subheadline || ''} onChange={e => updateField('subheadline', e.target.value)} className="w-full px-3 py-2 border rounded-lg h-20 text-sm" /></div>
          <TextSizeEditor label="Subheadline Size" value={config.subheadlineSize} customValue={config.subheadlineSizeCustom} onChange={v => updateField('subheadlineSize', v)} onCustomChange={v => updateField('subheadlineSizeCustom', v)} />
          <ColorPicker label="Subheadline Color" value={config.subheadlineColor} onChange={v => updateField('subheadlineColor', v)} />
          <div><label className="block text-sm font-medium mb-1">Alignment</label><div className="flex gap-2">{['left', 'center', 'right'].map(a => <button key={a} onClick={() => updateField('alignment', a)} className={`flex-1 py-2 border rounded capitalize text-sm ${config.alignment === a ? 'bg-primary text-white border-primary' : ''}`}>{a}</button>)}</div></div>
          <div><label className="block text-sm font-medium mb-1">Padding</label><div className="flex gap-2">{['small', 'medium', 'large'].map(p => <button key={p} onClick={() => updateField('padding', p)} className={`flex-1 py-2 border rounded capitalize text-sm ${config.padding === p ? 'bg-primary text-white border-primary' : ''}`}>{p}</button>)}</div></div>
          <BackgroundSettings config={config} updateField={updateField} />
        </>)}

        {/* ── TEXT ── */}
        {section.type === 'text' && (<>
          <div className="flex gap-2">
            <button onClick={() => updateField('useRichText', false)} className={`flex-1 py-2 border rounded text-xs ${!config.useRichText ? 'bg-primary text-white border-primary' : ''}`}>Plain</button>
            <button onClick={() => updateField('useRichText', true)} className={`flex-1 py-2 border rounded text-xs ${config.useRichText !== false ? 'bg-primary text-white border-primary' : ''}`}>Rich Text</button>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Content</label>
            {config.useRichText !== false
              ? <SimpleRichTextEditor value={config.content || ''} onChange={v => updateField('content', v)} />
              : <textarea value={config.content || ''} onChange={e => updateField('content', e.target.value)} className="w-full px-3 py-2 border border-border rounded-lg h-32 text-sm bg-card text-foreground" />
            }
          </div>
          {config.useRichText === false && (<>
            <TextSizeEditor label="Text Size" value={config.textSize} customValue={config.textSizeCustom} onChange={v => updateField('textSize', v)} onCustomChange={v => updateField('textSizeCustom', v)} />
            <ColorPicker label="Text Color" value={config.textColor} onChange={v => updateField('textColor', v)} />
          </>)}
          <div><label className="block text-sm font-medium mb-1">Alignment</label><div className="flex gap-2">{['left', 'center', 'right'].map(a => <button key={a} onClick={() => updateField('alignment', a)} className={`flex-1 py-2 border rounded capitalize text-sm ${config.alignment === a ? 'bg-primary text-white border-primary' : ''}`}>{a}</button>)}</div></div>
          <BackgroundSettings config={config} updateField={updateField} />
        </>)}

        {/* ── IMAGE ── */}
        {section.type === 'image' && (<>
          <Input label="Image URL" value={config.imageUrl || ''} onChange={e => updateField('imageUrl', e.target.value)} placeholder="https://..." />
          <div>
            <label className="block text-sm font-medium mb-1">Width: {config.width || 100}%</label>
            <input type="range" min="10" max="100" value={config.width || 100}
              onChange={e => updateField('width', parseInt(e.target.value))} className="w-full" />
          </div>
          <div><label className="block text-sm font-medium mb-1">Alignment</label><div className="flex gap-2">{['left', 'center', 'right'].map(a => <button key={a} onClick={() => updateField('alignment', a)} className={`flex-1 py-2 border rounded capitalize text-sm ${config.alignment === a ? 'bg-primary text-white border-primary' : ''}`}>{a}</button>)}</div></div>
          <Input label="Alt Text" value={config.alt || ''} onChange={e => updateField('alt', e.target.value)} />
          <Input label="Caption" value={config.caption || ''} onChange={e => updateField('caption', e.target.value)} />
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={config.rounded || false} onChange={e => updateField('rounded', e.target.checked)} className="rounded" />Rounded corners</label>
          <BackgroundSettings config={config} updateField={updateField} />
        </>)}

        {/* ── CTA ── */}
        {section.type === 'cta' && (<>
          <Input label="Button Text" value={config.text || ''} onChange={e => updateField('text', e.target.value)} />
          <Input label="Destination URL" value={config.destinationUrl || ''} onChange={e => updateField('destinationUrl', e.target.value)} placeholder="https://..." />
          <label className="flex items-center gap-2 p-3 border rounded-lg bg-purple-500/100/10 text-sm">
            <input type="checkbox" checked={config.isTestCta || false} onChange={e => updateField('isTestCta', e.target.checked)} className="rounded" />
            <div><span className="font-medium">Enable for A/B Testing</span><p className="text-xs text-muted-foreground">Assign to a test in Projects</p></div>
          </label>
          <ColorPicker label="Button Color" value={config.buttonColor} onChange={v => updateField('buttonColor', v)} />
          <ColorPicker label="Button Text Color" value={config.textColor} onChange={v => updateField('textColor', v)} />
          <div><label className="block text-sm font-medium mb-1">Size</label><div className="flex gap-2">{['small', 'medium', 'large'].map(s => <button key={s} onClick={() => updateField('size', s)} className={`flex-1 py-2 border rounded capitalize text-sm ${config.size === s ? 'bg-primary text-white border-primary' : ''}`}>{s}</button>)}</div></div>
          <div><label className="block text-sm font-medium mb-1">Style</label><div className="flex gap-2">{['solid', 'outline'].map(s => <button key={s} onClick={() => updateField('style', s)} className={`flex-1 py-2 border rounded capitalize text-sm ${config.style === s ? 'bg-primary text-white border-primary' : ''}`}>{s}</button>)}</div></div>
          <div><label className="block text-sm font-medium mb-1">Alignment</label><div className="flex gap-2">{['left', 'center', 'right'].map(a => <button key={a} onClick={() => updateField('alignment', a)} className={`flex-1 py-2 border rounded capitalize text-sm ${config.alignment === a ? 'bg-primary text-white border-primary' : ''}`}>{a}</button>)}</div></div>
          <hr />
          <label className="flex items-center gap-2 text-sm font-medium"><input type="checkbox" checked={config.secondaryEnabled || false} onChange={e => updateField('secondaryEnabled', e.target.checked)} className="rounded" />Secondary Button</label>
          {config.secondaryEnabled && (<>
            <Input label="Secondary Text" value={config.secondaryText || ''} onChange={e => updateField('secondaryText', e.target.value)} />
            <Input label="Secondary URL" value={config.secondaryUrl || ''} onChange={e => updateField('secondaryUrl', e.target.value)} placeholder="https://..." />
            <label className="flex items-center gap-2 p-2 border rounded-lg bg-purple-500/100/10 text-sm"><input type="checkbox" checked={config.secondaryIsTestCta || false} onChange={e => updateField('secondaryIsTestCta', e.target.checked)} className="rounded" />Enable for A/B Testing</label>
          </>)}
          <BackgroundSettings config={config} updateField={updateField} />
        </>)}

        {/* ── FEATURES ── */}
        {section.type === 'features' && (<>
          <Input label="Headline" value={config.headline || ''} onChange={e => updateField('headline', e.target.value)} />
          <TextSizeEditor label="Headline Size" value={config.headlineSize} customValue={config.headlineSizeCustom} onChange={v => updateField('headlineSize', v)} onCustomChange={v => updateField('headlineSizeCustom', v)} />
          <div><label className="block text-sm font-medium mb-1">Columns</label><div className="flex gap-2">{[2, 3, 4].map(c => <button key={c} onClick={() => updateField('columns', c)} className={`flex-1 py-2 border rounded text-sm ${config.columns === c ? 'bg-primary text-white border-primary' : ''}`}>{c}</button>)}</div></div>
          <div><label className="block text-sm font-medium mb-1">Item Alignment</label><div className="flex gap-2">{['left', 'center'].map(a => <button key={a} onClick={() => updateField('itemAlignment', a)} className={`flex-1 py-2 border rounded capitalize text-sm ${config.itemAlignment === a ? 'bg-primary text-white border-primary' : ''}`}>{a}</button>)}</div></div>
          <div>
            <label className="block text-sm font-medium mb-2">Features</label>
            {config.items?.map((item, i) => (
              <div key={i} className="border rounded p-2 mb-2 space-y-1.5">
                <div className="flex gap-2">
                  <select value={item.icon || 'zap'} onChange={e => updateArrayItem('items', i, 'icon', e.target.value)} className="w-20 px-1 py-1 border rounded text-xs">{Object.keys(ICON_MAP).map(ic => <option key={ic} value={ic}>{ic}</option>)}</select>
                  <input value={item.title} onChange={e => updateArrayItem('items', i, 'title', e.target.value)} className="flex-1 px-2 py-1 border border-border rounded text-sm bg-card text-foreground" placeholder="Title" />
                  <button onClick={() => removeArrayItem('items', i)} className="p-1 text-red-500 hover:bg-red-500/100/10 rounded"><Trash2 className="h-3.5 w-3.5" /></button>
                </div>
                <input value={item.description} onChange={e => updateArrayItem('items', i, 'description', e.target.value)} className="w-full px-2 py-1 border border-border rounded text-sm bg-card text-foreground" placeholder="Description" />
              </div>
            ))}
            <Button size="sm" variant="outline" onClick={() => addArrayItem('items', { icon: 'zap', title: 'Feature', description: 'Description' })}><Plus className="h-3 w-3 mr-1" />Add</Button>
          </div>
          <BackgroundSettings config={config} updateField={updateField} />
        </>)}

        {/* ── TESTIMONIALS ── */}
        {section.type === 'testimonials' && (<>
          <Input label="Headline" value={config.headline || ''} onChange={e => updateField('headline', e.target.value)} />
          <ColorPicker label="Card Background" value={config.cardBackgroundColor} onChange={v => updateField('cardBackgroundColor', v)} placeholder="#ffffff" />
          <div>
            <label className="block text-sm font-medium mb-2">Testimonials</label>
            {config.items?.map((item, i) => (
              <div key={i} className="border rounded p-2 mb-2 space-y-1.5">
                <div className="flex gap-2"><textarea value={item.quote} onChange={e => updateArrayItem('items', i, 'quote', e.target.value)} className="flex-1 px-2 py-1 border border-border rounded text-sm h-16 bg-card text-foreground" placeholder="Quote" /><button onClick={() => removeArrayItem('items', i)} className="p-1 text-red-500 hover:bg-red-500/100/10 rounded self-start"><Trash2 className="h-3.5 w-3.5" /></button></div>
                <div className="flex gap-2"><input value={item.author} onChange={e => updateArrayItem('items', i, 'author', e.target.value)} className="flex-1 px-2 py-1 border border-border rounded text-sm bg-card text-foreground" placeholder="Name" /><input value={item.role} onChange={e => updateArrayItem('items', i, 'role', e.target.value)} className="flex-1 px-2 py-1 border border-border rounded text-sm bg-card text-foreground" placeholder="Role" /></div>
                <input value={item.avatar || ''} onChange={e => updateArrayItem('items', i, 'avatar', e.target.value)} className="w-full px-2 py-1 border border-border rounded text-sm bg-card text-foreground" placeholder="Avatar URL (optional)" />
              </div>
            ))}
            <Button size="sm" variant="outline" onClick={() => addArrayItem('items', { quote: 'Great product!', author: 'Name', role: 'Role', avatar: '' })}><Plus className="h-3 w-3 mr-1" />Add</Button>
          </div>
          <BackgroundSettings config={config} updateField={updateField} />
        </>)}

        {/* ── PRICING ── */}
        {section.type === 'pricing' && (<>
          <Input label="Headline" value={config.headline || ''} onChange={e => updateField('headline', e.target.value)} />
          <div>
            <label className="block text-sm font-medium mb-2">Plans</label>
            {config.plans?.map((plan, i) => (
              <div key={i} className="border rounded p-2 mb-2 space-y-1.5">
                <div className="grid grid-cols-[1fr_auto_auto] gap-2 items-center"><input value={plan.name} onChange={e => updateArrayItem('plans', i, 'name', e.target.value)} className="px-2 py-1 border border-border rounded text-sm bg-card text-foreground" placeholder="Plan" /><input value={plan.price} onChange={e => updateArrayItem('plans', i, 'price', e.target.value)} className="w-16 px-2 py-1 border border-border rounded text-sm bg-card text-foreground" placeholder="$" /><button onClick={() => removeArrayItem('plans', i)} className="p-1 text-red-500 hover:bg-muted rounded"><Trash2 className="h-3.5 w-3.5" /></button></div>
                <input value={plan.period || 'month'} onChange={e => updateArrayItem('plans', i, 'period', e.target.value)} className="w-full px-2 py-1 border border-border rounded text-sm bg-card text-foreground" placeholder="Period (e.g. month)" />
                <input value={plan.cta || ''} onChange={e => updateArrayItem('plans', i, 'cta', e.target.value)} className="w-full px-2 py-1 border border-border rounded text-sm bg-card text-foreground" placeholder="CTA Text" />
                <label className="flex items-center gap-2 text-xs"><input type="checkbox" checked={plan.highlighted || false} onChange={e => updateArrayItem('plans', i, 'highlighted', e.target.checked)} className="rounded" />Highlighted</label>
              </div>
            ))}
            <Button size="sm" variant="outline" onClick={() => addArrayItem('plans', { name: 'Plan', price: '0', period: 'month', features: ['Feature 1'], cta: 'Get Started', highlighted: false })}><Plus className="h-3 w-3 mr-1" />Add Plan</Button>
          </div>
          <label className="flex items-center gap-2 p-3 border rounded-lg bg-purple-500/100/10 text-sm"><input type="checkbox" checked={config.isTestCta || false} onChange={e => updateField('isTestCta', e.target.checked)} className="rounded" /><div><span className="font-medium">Enable for A/B Testing</span><p className="text-xs text-muted-foreground">All plan CTAs use test variant URL</p></div></label>
          <BackgroundSettings config={config} updateField={updateField} />
        </>)}

        {/* ── FAQ ── */}
        {section.type === 'faq' && (<>
          <Input label="Headline" value={config.headline || ''} onChange={e => updateField('headline', e.target.value)} />
          <div>
            <label className="block text-sm font-medium mb-2">Questions</label>
            {config.items?.map((item, i) => (
              <div key={i} className="border rounded p-2 mb-2 space-y-1.5">
                <div className="flex gap-2"><input value={item.question} onChange={e => updateArrayItem('items', i, 'question', e.target.value)} className="flex-1 px-2 py-1 border border-border rounded text-sm bg-card text-foreground" placeholder="Question" /><button onClick={() => removeArrayItem('items', i)} className="p-1 text-red-500 hover:bg-red-500/100/10 rounded"><Trash2 className="h-3.5 w-3.5" /></button></div>
                <textarea value={item.answer} onChange={e => updateArrayItem('items', i, 'answer', e.target.value)} className="w-full px-2 py-1 border rounded text-sm h-16" placeholder="Answer" />
              </div>
            ))}
            <Button size="sm" variant="outline" onClick={() => addArrayItem('items', { question: 'Question?', answer: 'Answer.' })}><Plus className="h-3 w-3 mr-1" />Add</Button>
          </div>
          <BackgroundSettings config={config} updateField={updateField} />
        </>)}

        {/* ── VIDEO ── */}
        {section.type === 'video' && (<>
          <Input label="Headline" value={config.headline || ''} onChange={e => updateField('headline', e.target.value)} />
          <Input label="Video URL (YouTube/Vimeo embed)" value={config.videoUrl || ''} onChange={e => updateField('videoUrl', e.target.value)} placeholder="https://www.youtube.com/embed/..." />
          <BackgroundSettings config={config} updateField={updateField} />
        </>)}

        {/* ── COUNTDOWN ── */}
        {section.type === 'countdown' && (<>
          <Input label="Headline" value={config.headline || ''} onChange={e => updateField('headline', e.target.value)} />
          <div>
            <label className="block text-sm font-medium mb-1">End Date & Time</label>
            <input
              type="datetime-local"
              value={pendingEndDate}
              onChange={e => setPendingEndDate(e.target.value)}
              className="w-full px-3 py-2 border border-border rounded-lg text-sm bg-card text-foreground"
            />
            <button
              onClick={() => { updateField('endDate', pendingEndDate); }}
              className="mt-2 w-full flex items-center justify-center gap-2 py-2 bg-primary text-white rounded-lg text-sm font-medium hover:bg-primary/90"
            >
              <Save className="h-3.5 w-3.5" /> Apply Date
            </button>
            {config.endDate && (
              <p className="text-xs text-green-600 mt-1">✓ Set to: {new Date(config.endDate).toLocaleString()}</p>
            )}
          </div>
          <Input label="Expired Message" value={config.expiredText || ''} onChange={e => updateField('expiredText', e.target.value)} placeholder="Offer Expired" />
          <BackgroundSettings config={config} updateField={updateField} />
        </>)}

        {/* ── DIVIDER ── */}
        {section.type === 'divider' && (<>
          <div><label className="block text-sm font-medium mb-1">Style</label><div className="flex gap-2">{['line', 'dots'].map(s => <button key={s} onClick={() => updateField('style', s)} className={`flex-1 py-2 border rounded capitalize text-sm ${config.style === s ? 'bg-primary text-white border-primary' : ''}`}>{s}</button>)}</div></div>
          <div><label className="block text-sm font-medium mb-1">Spacing</label><div className="flex gap-2">{['small', 'medium', 'large'].map(s => <button key={s} onClick={() => updateField('spacing', s)} className={`flex-1 py-2 border rounded capitalize text-sm ${config.spacing === s ? 'bg-primary text-white border-primary' : ''}`}>{s}</button>)}</div></div>
          <ColorPicker label="Line Color" value={config.color} onChange={v => updateField('color', v)} />
          <BackgroundSettings config={config} updateField={updateField} />
        </>)}

        {/* ── SPACER ── */}
        {section.type === 'spacer' && (<>
          <div><label className="block text-sm font-medium mb-1">Height</label><div className="flex gap-2">{['small', 'medium', 'large'].map(h => <button key={h} onClick={() => updateField('height', h)} className={`flex-1 py-2 border rounded capitalize text-sm ${config.height === h ? 'bg-primary text-white border-primary' : ''}`}>{h}</button>)}</div></div>
          <BackgroundSettings config={config} updateField={updateField} />
        </>)}

        {/* ── HTML ── */}
        {section.type === 'html' && (<>
          <div><label className="block text-sm font-medium mb-1">HTML Code</label><textarea value={config.code || ''} onChange={e => updateField('code', e.target.value)} className="w-full px-3 py-2 border rounded-lg h-48 font-mono text-xs" placeholder="<div>...</div>" /></div>
          <p className="text-xs text-amber-600 bg-amber-50 p-2 rounded">⚠️ HTML is rendered as-is. Use caution with scripts.</p>
          <BackgroundSettings config={config} updateField={updateField} />
        </>)}

      </div>
    </div>
  );
};

// ==================== STANDARD PAGE PREVIEW ====================
export const StandardPagePreview = ({
  sections, theme, footer, stickyHeader, socialProof, popup,
  editingSection, setEditingSection, onSectionMove, onSectionDelete,
  onSectionUpdate,
  previewTab = 'page', onSetPreviewTab,
}) => {
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const [dragOverCol, setDragOverCol] = useState(null);   // for container column drops
  const [activeContainerId, setActiveContainerId] = useState(null); // which container is receiving
  const primaryColor = theme?.primaryColor || '#10B981';

  const handleDragStart = (e, i) => { setDraggedIndex(i); e.dataTransfer.effectAllowed = 'move'; };
  const handleDragOver = (e, i) => { e.preventDefault(); if (draggedIndex !== null && draggedIndex !== i) setDragOverIndex(i); };
  const handleDragEnd = () => {
    if (draggedIndex !== null && dragOverIndex !== null && draggedIndex !== dragOverIndex) onSectionMove(draggedIndex, dragOverIndex);
    setDraggedIndex(null); setDragOverIndex(null);
  };

  // ── Drop a top-level section INTO a container column ─────────────────────
  const handleColumnDrop = (containerId, colIdx, e) => {
    setDragOverCol(null);
    setActiveContainerId(null);
    if (draggedIndex === null) return;

    const dragged = sections[draggedIndex];
    if (!dragged || dragged.id === containerId || dragged.type === 'container') return;

    // Add to container column
    const container = sections.find(s => s.id === containerId);
    if (!container) return;
    const colContent = [...(container.config.columnContent || [])];
    while (colContent.length <= colIdx) colContent.push([]);
    colContent[colIdx] = [...(colContent[colIdx] || []), { ...dragged, id: `${dragged.id}-c${Date.now()}` }];

    onSectionUpdate?.({ ...container, config: { ...container.config, columnContent: colContent } });
    onSectionDelete(dragged.id);
    setDraggedIndex(null);
  };

  const handleRemoveChild = (containerId, colIdx, childIdx) => {
    const container = sections.find(s => s.id === containerId);
    if (!container) return;
    const colContent = (container.config.columnContent || []).map((col, ci) =>
      ci === colIdx ? col.filter((_, ii) => ii !== childIdx) : col
    );
    onSectionUpdate?.({ ...container, config: { ...container.config, columnContent: colContent } });
  };

  return (
    <div>
      {/* Sticky header */}
      {stickyHeader?.enabled && (
        <div className="sticky top-0 z-20 py-2 px-4 text-sm"
          style={{ backgroundColor: stickyHeader.backgroundColor || primaryColor, color: stickyHeader.textColor || '#ffffff' }}>
          <div className={`flex items-center gap-4 ${stickyHeader.alignment === 'left' ? 'justify-start' : stickyHeader.alignment === 'right' ? 'justify-end' : 'justify-center'}`}>
            {stickyHeader.text && <span>{stickyHeader.text}</span>}
            {stickyHeader.countdownEnabled && stickyHeader.countdownEndDate && (
              <span className="font-bold">
                <LiveCountdown endDate={stickyHeader.countdownEndDate} expiredText={stickyHeader.countdownExpiredText || ''} primaryColor={stickyHeader.textColor || '#ffffff'} />
              </span>
            )}
          </div>
        </div>
      )}

      {sections.length === 0 ? (
        <div className="py-32 text-center text-muted-foreground">
          <Layout className="h-16 w-16 mx-auto mb-4 opacity-30" />
          <p className="text-lg">Add sections from the left panel</p>
          <p className="text-sm">or choose a template</p>
        </div>
      ) : (
        sections.map((section, idx) => (
          <div key={section.id} draggable
            onDragStart={e => handleDragStart(e, idx)} onDragOver={e => handleDragOver(e, idx)} onDragEnd={handleDragEnd}
            className={`relative group ${dragOverIndex === idx ? 'border-t-4 border-primary' : ''} ${draggedIndex === idx ? 'opacity-50' : ''}`}
          >
              <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity z-10 flex gap-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg shadow-lg p-1">
                <button onClick={() => setEditingSection(section)} className="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded text-gray-700 dark:text-gray-200" title="Edit"><Settings className="h-4 w-4" /></button>
                <button onClick={() => idx > 0 && onSectionMove(idx, idx - 1)} disabled={idx === 0} className="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded text-gray-700 dark:text-gray-200 disabled:opacity-30" title="Up"><ChevronUp className="h-4 w-4" /></button>
                <button onClick={() => idx < sections.length - 1 && onSectionMove(idx, idx + 1)} disabled={idx === sections.length - 1} className="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded text-gray-700 dark:text-gray-200 disabled:opacity-30" title="Down"><ChevronDown className="h-4 w-4" /></button>
                <button onClick={() => onSectionDelete(section.id)} className="p-1.5 hover:bg-red-50 dark:hover:bg-red-500/20 rounded text-red-500" title="Delete"><Trash2 className="h-4 w-4" /></button>
              </div>
              <div className="absolute top-1/2 left-2 -translate-y-1/2 opacity-0 group-hover:opacity-100 cursor-grab">
                <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded p-0.5 shadow-md">
                  <GripVertical className="h-5 w-5 text-gray-500 dark:text-gray-300" />
                </div>
              </div>
              <div className="group-hover:outline group-hover:outline-2 group-hover:outline-primary/30 group-hover:outline-offset-[-2px]">
                {section.type === 'container' ? (
                  <ContainerRenderer
                    section={section}
                    theme={theme}
                    onColumnDrop={(colIdx, e) => handleColumnDrop(section.id, colIdx, e)}
                    onColumnDragOver={colIdx => { setDragOverCol(colIdx); setActiveContainerId(section.id); }}
                    dragOverCol={activeContainerId === section.id ? dragOverCol : null}
                    onRemoveChild={(colIdx, childIdx) => handleRemoveChild(section.id, colIdx, childIdx)}
                  />
                ) : (
                  <SectionRenderer section={section} config={section.config} theme={theme} />
                )}
              </div>
          </div>
        ))
      )}

      {/* Footer */}
      {footer?.enabled && (
        <div className="border-t p-4 text-center text-sm" style={{ backgroundColor: footer.backgroundColor, color: footer.textColor }}>
          {(footer.showPrivacyLink || footer.showTermsLink) && (
            <div className="flex justify-center gap-4 opacity-60 mb-2">
              {footer.showPrivacyLink && <span className="hover:underline cursor-pointer">Privacy Policy</span>}
              {footer.showTermsLink && <span className="hover:underline cursor-pointer">Terms of Service</span>}
            </div>
          )}
          {footer.customText && <p className="opacity-50 text-xs">{footer.customText}</p>}
        </div>
      )}

      {/* Social proof badge (preview) */}
      {socialProof?.enabled && (
        <div className={`fixed z-40 max-w-xs rounded-lg shadow-lg p-3 flex items-center gap-3 ${socialProof.position === 'bottom-right' ? 'bottom-4 right-4' : 'bottom-4 left-4'}`}
          style={{ backgroundColor: socialProof.backgroundColor || '#1f2937', color: socialProof.textColor || '#ffffff' }}>
          <div className="w-8 h-8 bg-green-500/100 rounded-full flex items-center justify-center flex-shrink-0"><Users className="h-4 w-4 text-white" /></div>
          <div className="text-sm">
            <span className="font-bold">{Math.floor(Math.random() * ((socialProof.maxNumber || 50) - (socialProof.minNumber || 10) + 1)) + (socialProof.minNumber || 10)}</span>
            {' '}{socialProof.text || 'people are viewing this page'}
          </div>
        </div>
      )}

      {/* Popup preview — tab-controlled, only shown when previewTab === 'popup' */}
      {popup?.enabled && previewTab === 'popup' && (
        <div className="absolute inset-0 bg-black/60 flex items-center justify-center z-50">
          <div className="bg-card dark:bg-card rounded-2xl shadow-2xl p-8 max-w-sm w-full mx-4 text-center">
            {popup.title && <h3 className="text-xl font-bold mb-3" style={{ color: popup.titleColor || '#dc2626' }}>{popup.title}</h3>}
            {popup.message && <p className="text-foreground/70 mb-6">{popup.message}</p>}
            {popup.ctaText && (
              <button className="w-full py-3 rounded-lg font-semibold text-white" style={{ backgroundColor: primaryColor }}>
                {popup.ctaText}
              </button>
            )}
            <button onClick={() => onSetPreviewTab?.('page')} className="mt-3 text-xs text-muted-foreground/70 underline">Close preview</button>
          </div>
        </div>
      )}
    </div>
  );
};
